import { Megaphone } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { useViewerUserId } from '@apps/web/features/phoebe_chat_v3/ui/viewer_user_context';

type OutreachStartedTextProps = Readonly<{
    readonly automatic: boolean;
    readonly userId: string | null;
    readonly displayName: string | null;
    // The outreach's entity pill when the caller has one (the domain-event
    // composer); the poke path has no outreach reference and reads "the
    // outreach" instead.
    readonly subject?: ReactNode;
}>;

// Mirrors StopRequestLine's attribution: the viewer's own start reads
// "You", a collaborator is named once the event carries their name, and
// an automatic or unresolved sender stays unattributed. Shared by the
// legacy outreach-start poke and the outreach.status_changed domain
// event so the two paths cannot drift apart.
export function OutreachStartedText({
    automatic,
    userId,
    displayName,
    subject,
}: OutreachStartedTextProps) {
    const viewerUserId = useViewerUserId();
    if (subject === undefined) {
        const copy = automatic
            ? 'The outreach started automatically'
            : userId !== null && userId === viewerUserId
              ? 'You started the outreach'
              : displayName !== null
                ? `${displayName} started the outreach`
                : 'The outreach was started';
        return <Fragment>{copy}</Fragment>;
    }
    if (automatic) {
        return <Fragment>{subject} started automatically</Fragment>;
    }
    if (userId !== null && userId === viewerUserId) {
        return <Fragment>You started {subject}</Fragment>;
    }
    if (displayName !== null) {
        return (
            <Fragment>
                {displayName} started {subject}
            </Fragment>
        );
    }
    return <Fragment>{subject} was started</Fragment>;
}

type OutreachStartedActionLineProps = Readonly<{
    readonly automatic: boolean;
    readonly userId: string | null;
    readonly displayName: string | null;
}>;

export function OutreachStartedActionLine({
    automatic,
    userId,
    displayName,
}: OutreachStartedActionLineProps) {
    return (
        <EventLine glyph={<Megaphone />}>
            <OutreachStartedText
                automatic={automatic}
                userId={userId}
                displayName={displayName}
            />
        </EventLine>
    );
}
